// Single source of truth for permission slugs (mirrors backend/permissions.py)
//
// Used by:
//   - SidebarLayout (filter visible items)
//   - useCan() hook for runtime checks
//   - Users page (build the granular toggle UI)
//   - Page-level guards via <RequirePermission key="…">

export const PAGE_PERMISSIONS = [
  { key: 'dashboard',                  label: 'Dashboard' },
  { key: 'sales',                      label: 'Sales (Kanban)' },
  { key: 'sales_pipeline',             label: 'Sales Pipeline' },
  { key: 'sales_calendar',             label: 'Sales Calendar' },
  { key: 'purchases',                  label: 'Purchases' },
  { key: 'purchase_calendar',          label: 'Purchase Calendar' },
  { key: 'procurement_intelligence',   label: 'Procurement Intelligence' },
  { key: 'inventory',                  label: 'Inventory' },
  { key: 'item_codes',                 label: 'Item Codes' },
  { key: 'transformations',            label: 'Transformations' },
  { key: 'production',                 label: 'Production' },
  { key: 'quality_check',              label: 'Quality Check' },
  { key: 'recipes',                    label: 'Recipes' },
  { key: 'rnd',                        label: 'R&D / Trials' },
  { key: 'cashflow',                   label: 'Cashflow (full ledger)' },
  { key: 'debtors',                    label: 'Debtors (receivables)' },
  { key: 'creditors',                  label: 'Creditors (payables)' },
  { key: 'bank_reconciliation',        label: 'Bank Reconciliation' },
  { key: 'stock_debts',                label: 'Stock Debts' },
  { key: 'loans',                      label: 'Loans & Liabilities' },
  { key: 'overdrafts',                 label: 'Overdrafts (OD limits)' },
  { key: 'credit_cards',               label: 'Credit Cards' },
  { key: 'accounting',                 label: 'Accounting' },
  { key: 'vouchers',                   label: 'Vouchers / Invoices' },
  { key: 'expenses',                   label: 'Operating Expenses (admin)' },
  { key: 'profitability',              label: 'Master Profitability (admin)' },
  { key: 'workforce',                  label: 'Workforce' },
  { key: 'compliance',                 label: 'Compliance' },
  { key: 'reports',                    label: 'Reports' },
  { key: 'financial_reports',          label: 'Financial Reports' },
  { key: 'tasks',                      label: 'Tasks' },
  { key: 'samples',                    label: 'Samples' },
  { key: 'ai_memory',                  label: 'AI Memory' },
  { key: 'creditor_reminders',         label: 'Vendor Payment Reminders' },
  { key: 'debtor_reminders',            label: 'Debtor Collection Cockpit' },
  { key: 'price_audit_log',            label: 'Price Audit Log' },
  { key: 'user_management',            label: 'User Management' },
  { key: 'backup',                     label: 'Backup & Export' },
  { key: 'historical_data',            label: 'Historical Data' },
  { key: 'admin_portal_orders',        label: 'Admin · Portal Orders' },
  { key: 'admin_portal_accounts',      label: 'Admin · Portal Accounts' },
  { key: 'notifications',              label: 'Notifications' },
];

export const FEATURE_PERMISSIONS = [
  { key: 'feature_ti_chat',            label: 'TI AI Chat' },
  { key: 'feature_voice_input',        label: 'Voice Input (microphone)' },
  { key: 'feature_notifications_bell', label: 'Notifications Bell' },
  { key: 'feature_grievances_widget',  label: 'Grievances Widget' },
  { key: 'feature_mail_dropdown',      label: 'Mail / Inbox Dropdown' },
  { key: 'feature_daily_lesson',       label: 'Daily Lesson Card' },
  { key: 'feature_standup',            label: '9 AM Standup Modal' },
  { key: 'feature_forecast_buy_now',   label: 'Buy-Now from Forecast' },
  { key: 'feature_ai_features',        label: 'All AI Features (umbrella)' },
  { key: 'feature_history_logs',       label: 'History / Audit Logs' },
  { key: 'feature_backup_now',         label: 'Trigger Backup Now' },
  { key: 'feature_excel_export',       label: 'Excel Downloads' },
  { key: 'feature_csv_export',         label: 'CSV Downloads' },
];

export const DATA_PERMISSIONS = [
  { key: 'can_see_prices',             label: 'Can see prices' },
  { key: 'can_see_costs',              label: 'Can see costs / margins' },
  { key: 'can_see_margins',            label: 'Can see profit margins' },
  { key: 'can_see_past_data',          label: 'Past archives (>90 days)' },
  { key: 'can_see_other_user_orders',  label: 'Other users\' orders' },
  { key: 'can_see_item_codes',         label: 'Item codes (admin alias)' },
];

export const ACTION_PERMISSIONS = [
  { key: 'can_create_orders',          label: 'Create sales orders' },
  { key: 'can_edit_orders',            label: 'Edit sales orders' },
  { key: 'can_delete_orders',          label: 'Delete sales orders' },
  { key: 'can_create_invoices',        label: 'Create invoices' },
  { key: 'can_delete_invoices',        label: 'Delete invoices' },
  { key: 'can_adjust_stock',           label: 'Adjust raw-material stock' },
  { key: 'can_delete_lots',            label: 'Delete inventory lots' },
  { key: 'can_create_transactions',    label: 'Create cashflow entries' },
  { key: 'can_delete_transactions',    label: 'Delete cashflow entries' },
  { key: 'can_create_users',           label: 'Create users' },
  { key: 'can_edit_users',             label: 'Edit users' },
  { key: 'can_delete_users',           label: 'Delete users' },
  { key: 'can_edit_recipes',           label: 'Edit recipes' },
  { key: 'can_snapshot_recipes',       label: 'Snapshot recipe baselines' },
  { key: 'can_create_pos',             label: 'Create purchase orders' },
  { key: 'can_approve_pos',            label: 'Approve purchase orders' },
];

export const WIDGET_PERMISSIONS = [
  { key: 'widget_kpi_cards',                label: 'KPI Cards (Revenue, Orders, Inventory, …)' },
  { key: 'widget_sales_pipeline',           label: 'Sales Pipeline' },
  { key: 'widget_grievances',               label: 'Grievances / Issue Notices' },
  { key: 'widget_tip_of_day',               label: 'Lesson / Tip of the Day' },
  { key: 'widget_feasibility_risks',        label: 'AI Feasibility Risks' },
  { key: 'widget_product_code_analytics',   label: 'Product Code Analytics' },
  { key: 'widget_cash_balance',             label: 'Current Cash Balance' },
  { key: 'widget_payment_reminders',        label: 'Vendor Payment Reminders' },
  { key: 'widget_collection_cockpit',       label: 'Debtor Collection Cockpit' },
  { key: 'widget_stock_debts',              label: 'Stock Debts' },
  { key: 'widget_cost_variance',            label: 'Cost Variance Alerts' },
  { key: 'widget_expiry_alerts',            label: 'Expiry Alerts' },
  { key: 'widget_delay_tracking',           label: 'Delay Tracking' },
  { key: 'widget_sales_entity_breakdown',   label: 'Sales Entity (GST vs Non-GST) Breakdown' },
  { key: 'widget_pending_intel',            label: 'Pending Orders Intelligence' },
  { key: 'widget_upcoming_dispatches',      label: 'Upcoming Dispatches' },
  { key: 'widget_priority_todos',           label: 'Priority To-Dos' },
  { key: 'widget_dispatch_status',          label: 'Dispatch Status' },
  { key: 'widget_revenue_chart',            label: 'Revenue Trend Chart' },
  { key: 'widget_alerts',                   label: 'System Alerts' },
  { key: 'widget_top_customers',            label: 'Top Customers' },
  { key: 'widget_recent_batches',           label: 'Recent Production Batches' },
  { key: 'widget_low_stock',                label: 'Low Stock Items' },
];

export const ALL_PERMISSIONS = [
  ...PAGE_PERMISSIONS,
  ...FEATURE_PERMISSIONS,
  ...DATA_PERMISSIONS,
  ...ACTION_PERMISSIONS,
  ...WIDGET_PERMISSIONS,
];

export const ROLE_OPTIONS = [
  { value: 'owner',      label: 'Owner — full access' },
  { value: 'admin',      label: 'Admin — full access' },
  { value: 'manager',    label: 'Manager — operations' },
  { value: 'sales',      label: 'Sales — orders + dashboard' },
  { value: 'production', label: 'Production — factory floor' },
  { value: 'purchase',   label: 'Purchase — procurement' },
  { value: 'accountant', label: 'Accountant — finance only' },
  { value: 'staff',      label: 'Staff — minimal access' },
  { value: 'custom',     label: 'Custom — pick specific permissions' },
];

/**
 * Check if a user object has a permission slug.
 * Owners and admins always pass — even if the explicit flag is missing.
 */
export function userCan(user, key) {
  if (!user) return false;
  if (user.role === 'owner' || user.role === 'admin') return true;
  return Boolean(user.permissions && user.permissions[key]);
}
